import React, { useState, useEffect, useCallback } from 'react';
import { taskApi } from '../../../../tasks/api/taskApi';
import { useMatterBilling } from '../../../hooks/useMatterBilling';
import TimeTracker from '../../TimeTracker';
import CourtMandatedBadge from '../../../../tasks/components/CourtMandatedBadge';
import type { Task, TaskDependency } from '../../../../tasks/types/task.types';
import type { TimeEntry } from '../../../../billing/types/billing.types';

interface GanttTaskDetailPanelProps {
  taskId: string;
  matterId: string;
  onClose: () => void;
  onTaskUpdated: () => void;
  onEditTask: (taskId: string) => void;
}

interface TaskAttachmentItem {
  id: string;
  file_name: string;
  file_size: number;
  mime_type: string;
  uploaded_by: string;
  created_at: string;
}

const STATUS_BADGES: Record<string, { label: string; className: string }> = {
  todo: { label: 'To Do', className: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300' },
  in_progress: { label: 'In Progress', className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
  review: { label: 'In Review', className: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300' },
  done: { label: 'Done', className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' },
  archived: { label: 'Archived', className: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-500' },
};

const PRIORITY_BADGES: Record<string, { label: string; className: string }> = {
  urgent: { label: 'Urgent', className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
  high: { label: 'High', className: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300' },
  medium: { label: 'Medium', className: 'bg-blue-100 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400' },
  low: { label: 'Low', className: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400' },
};

function fmtDate(dateStr: string | undefined | null): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function getOverdueDays(dueDate: string): number {
  const due = new Date(dueDate);
  const now = new Date();
  return Math.floor((now.getTime() - due.getTime()) / (1000 * 60 * 60 * 24));
}

function isUtbmsCode(code?: string): boolean {
  if (!code) return false;
  return /^[A-Z]\d{3}/.test(code);
}

function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 KB';
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.max(1, Math.round(kb))} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

const GanttTaskDetailPanel: React.FC<GanttTaskDetailPanelProps> = ({
  taskId,
  matterId,
  onClose,
  onTaskUpdated,
  onEditTask,
}) => {
  const [task, setTask] = useState<Task | null>(null);
  const [loading, setLoading] = useState(true);
  const [deps, setDeps] = useState<{ dependencies: TaskDependency[]; dependedOnBy: TaskDependency[] }>({ dependencies: [], dependedOnBy: [] });
  const [attachments, setAttachments] = useState<TaskAttachmentItem[]>([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [attachmentsError, setAttachmentsError] = useState(false);
  const [markingComplete, setMarkingComplete] = useState(false);

  const {
    activeTimer,
    createAndStartTimer,
    startTimer,
    pauseTimer,
    stopTimer,
    timeEntries,
  } = useMatterBilling(matterId);

  // Fetch task detail
  const fetchTask = useCallback(async () => {
    try {
      setLoading(true);
      const result = await taskApi.getById(taskId) as any;
      setTask(result.task || result.data?.task || null);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  }, [taskId]);

  // Fetch dependencies
  const fetchDeps = useCallback(async () => {
    try {
      const result = await taskApi.getDependencies(taskId) as any;
      setDeps({
        dependencies: result.dependencies || result.data?.dependencies || [],
        dependedOnBy: result.dependedOnBy || result.data?.dependedOnBy || [],
      });
    } catch {
      // silently fail
    }
  }, [taskId]);

  // Fetch attachments
  const fetchAttachments = useCallback(async () => {
    try {
      setAttachmentsLoading(true);
      setAttachmentsError(false);
      const result = await taskApi.getAttachments(taskId) as any;
      setAttachments(result.attachments || result.data?.attachments || []);
    } catch {
      setAttachments([]);
      setAttachmentsError(true);
    } finally {
      setAttachmentsLoading(false);
    }
  }, [taskId]);

  useEffect(() => {
    fetchTask();
    fetchDeps();
    fetchAttachments();
  }, [fetchTask, fetchDeps, fetchAttachments]);

  // Close on Escape
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  // Mark complete handler
  const handleMarkComplete = async () => {
    if (!task) return;
    try {
      setMarkingComplete(true);
      await taskApi.updateStatus(taskId, 'done');
      onTaskUpdated();
      fetchTask();
    } catch {
      // silently fail
    } finally {
      setMarkingComplete(false);
    }
  };

  // Filter recent time entries for this matter (last 5)
  const recentTimeEntries = timeEntries
    .sort((a: TimeEntry, b: TimeEntry) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    .slice(0, 5);

  const isOverdue = task?.due_date && task.status !== 'done' && task.status !== 'archived' &&
    new Date(task.due_date) < new Date();
  const overdueDays = task?.due_date && isOverdue ? getOverdueDays(task.due_date) : 0;
  const statusBadge = STATUS_BADGES[task?.status || 'todo'] || STATUS_BADGES.todo;
  const priorityBadge = PRIORITY_BADGES[task?.priority || 'medium'] || PRIORITY_BADGES.medium;
  const isMandated = task?.court_metadata?.is_court_mandated;
  const forms = task?.court_metadata?.required_forms || [];

  return (
    <div
      className="flex-shrink-0 w-[500px] border-l border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 overflow-y-auto"
      style={{ animation: 'ganttPanelSlideIn 220ms cubic-bezier(0.2, 0.8, 0.2, 1) both' }}
    >
      <style>{`
        @keyframes ganttPanelSlideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        /* Force TimeTracker form to use 2 columns (description+rate on row 1, buttons on row 2)
           instead of Tailwind's viewport-based sm:grid-cols-3 which overflows in a 500px panel */
        .gantt-panel-timer .grid {
          grid-template-columns: 1fr 1fr !important;
        }
      `}</style>

      {/* Panel Header */}
      <div className="sticky top-0 z-10 flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
        <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Task Detail</span>
        <button
          onClick={onClose}
          className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
        </div>
      ) : !task ? (
        <div className="p-4 text-sm text-gray-500 dark:text-gray-400">Task not found.</div>
      ) : (
        <div className="p-4 space-y-5">

          {/* Section 1: Header */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white leading-tight mb-2">
              {task.title}
            </h2>
            <div className="flex flex-wrap items-center gap-2">
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusBadge.className}`}>
                {statusBadge.label}
              </span>
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${priorityBadge.className}`}>
                {priorityBadge.label}
              </span>
              {isMandated && (
                <CourtMandatedBadge
                  mandateReference={task.court_metadata?.mandate_reference}
                  statuteReference={task.court_metadata?.statutory_deadline?.reference}
                />
              )}
            </div>
          </div>

          {/* Section 2: Key Dates */}
          <div className="space-y-1.5">
            <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Dates</h3>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="text-gray-500 dark:text-gray-400">Start:</span>{' '}
                <span className="text-gray-900 dark:text-white">{fmtDate(task.start_date)}</span>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400">Due:</span>{' '}
                <span className={isOverdue ? 'text-red-600 dark:text-red-400 font-medium' : 'text-gray-900 dark:text-white'}>
                  {fmtDate(task.due_date)}
                </span>
              </div>
            </div>
            {isOverdue && (
              <div className="flex items-center gap-1.5 text-xs text-red-600 dark:text-red-400 font-medium">
                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {overdueDays} day{overdueDays !== 1 ? 's' : ''} overdue
              </div>
            )}
          </div>

          {/* Section 3: Assignment */}
          <div className="space-y-1.5">
            <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Assigned To</h3>
            {task.assignee ? (
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-xs font-medium text-blue-700 dark:text-blue-300">
                  {(task.assignee.full_name || task.assignee.email || '?').charAt(0).toUpperCase()}
                </div>
                <span className="text-sm text-gray-900 dark:text-white">
                  {task.assignee.full_name || task.assignee.email}
                </span>
              </div>
            ) : (
              <span className="text-sm text-gray-400 dark:text-gray-500 italic">Unassigned</span>
            )}
          </div>

          {/* Section 4: Court Forms */}
          <div className="space-y-1.5">
            <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Court Forms</h3>
            {forms.length > 0 ? (
              <ul className="space-y-1.5">
                {forms.map((form) => (
                  <li
                    key={form.form_number}
                    className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-gray-50 dark:bg-gray-800 text-sm"
                  >
                    <span className="font-medium text-blue-600 dark:text-blue-400 flex-shrink-0">
                      {form.form_number}
                    </span>
                    <span className="text-gray-700 dark:text-gray-300 truncate flex-1">
                      {form.form_name}
                    </span>
                    {form.is_mandatory && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-600 dark:bg-red-900/20 dark:text-red-400 flex-shrink-0">
                        Required
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <span className="text-sm text-gray-400 dark:text-gray-500 italic">No court forms attached</span>
            )}
          </div>

          {/* Section 5: Documents */}
          <div className="space-y-1.5">
            <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Documents</h3>
            {attachmentsLoading ? (
              <div className="flex items-center gap-2 py-1">
                <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-blue-600" />
                <span className="text-sm text-gray-500 dark:text-gray-400">Loading documents...</span>
              </div>
            ) : attachmentsError ? (
              <span className="text-sm text-red-500 dark:text-red-400">Unable to load documents</span>
            ) : attachments.length === 0 ? (
              <span className="text-sm text-gray-400 dark:text-gray-500 italic">No documents attached</span>
            ) : (
              <ul className="space-y-1.5">
                {attachments.map((attachment) => {
                  const isPdf = attachment.mime_type === 'application/pdf';
                  return (
                    <li
                      key={attachment.id}
                      className="flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg bg-gray-50 dark:bg-gray-800 text-sm"
                    >
                      <div className={`flex-shrink-0 ${isPdf ? 'text-red-500 dark:text-red-400' : 'text-blue-500 dark:text-blue-400'}`}>
                        {isPdf ? (
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M4 2a2 2 0 00-2 2v12a2 2 0 002 2h12a2 2 0 002-2V8l-6-6H4zm7 1.5L16.5 9H12a1 1 0 01-1-1V3.5z" />
                          </svg>
                        ) : (
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M4 2a2 2 0 00-2 2v12a2 2 0 002 2h12a2 2 0 002-2V8l-6-6H4zm6 1v5a2 2 0 002 2h5v6H4V3h6z" />
                          </svg>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-gray-800 dark:text-gray-200 truncate">{attachment.file_name}</div>
                        <div className="text-[11px] text-gray-500 dark:text-gray-400">
                          {formatFileSize(Number(attachment.file_size))} &middot; {fmtDate(attachment.created_at)}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Section 6: Time Entries */}
          <div className="space-y-2">
            <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Time Tracking</h3>
            <div className="gantt-panel-timer">
              <TimeTracker
                compact
                activeTimer={activeTimer}
                onStart={(dto) => createAndStartTimer({
                  ...dto,
                  activity_category: dto.activity_category || (!isUtbmsCode(task.billing_code) ? task.billing_code : undefined) || undefined,
                  utbms_code: dto.utbms_code || (isUtbmsCode(task.billing_code) ? task.billing_code : undefined) || undefined,
                })}
                onPause={pauseTimer}
                onResume={startTimer}
                onStop={stopTimer}
                onComplete={async (entry) => {
                  const loggedHours = Number(entry.duration) || 0;
                  if (loggedHours > 0) {
                    const currentHours = task.actual_hours || 0;
                    await taskApi.update(task.id, { actual_hours: currentHours + loggedHours });
                    onTaskUpdated();
                    fetchTask();
                  }
                }}
                defaultDescription={task.title}
                defaultActivityCategory={!isUtbmsCode(task.billing_code) ? task.billing_code : undefined}
                defaultUtbmsCode={isUtbmsCode(task.billing_code) ? task.billing_code : undefined}
              />
            </div>
            {task.actual_hours != null && task.actual_hours > 0 && (
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Total logged: <span className="font-medium text-gray-700 dark:text-gray-300">{task.actual_hours.toFixed(1)}h</span>
                {task.estimated_hours != null && (
                  <span> / {task.estimated_hours.toFixed(1)}h estimated</span>
                )}
              </div>
            )}
            {recentTimeEntries.length > 0 && (
              <div className="space-y-1">
                <div className="text-xs text-gray-500 dark:text-gray-400 font-medium">Recent entries:</div>
                {recentTimeEntries.map((entry: TimeEntry) => (
                  <div key={entry.time_entry_id} className="flex items-center justify-between text-xs px-2 py-1 bg-gray-50 dark:bg-gray-800 rounded">
                    <span className="text-gray-700 dark:text-gray-300 truncate flex-1 mr-2">
                      {entry.description || '(no description)'}
                    </span>
                    <span className="text-gray-500 dark:text-gray-400 flex-shrink-0">
                      {Number(entry.duration).toFixed(1)}h &middot; {new Date(entry.entry_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Section 7: Dependencies (bonus info) */}
          {(deps.dependencies.length > 0 || deps.dependedOnBy.length > 0) && (
            <div className="space-y-1.5">
              <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Dependencies</h3>
              {deps.dependencies.length > 0 && (
                <div className="text-xs text-gray-600 dark:text-gray-400">
                  <span className="font-medium">Depends on:</span>{' '}
                  {deps.dependencies.map(d => d.dependsOnTask?.title || d.depends_on_task_id).join(', ')}
                </div>
              )}
              {deps.dependedOnBy.length > 0 && (
                <div className="text-xs text-gray-600 dark:text-gray-400">
                  <span className="font-medium">Blocks:</span>{' '}
                  {deps.dependedOnBy.map(d => d.task?.title || d.task_id).join(', ')}
                </div>
              )}
            </div>
          )}

          {/* Section 8: Actions */}
          <div className="space-y-2 pt-2 border-t border-gray-200 dark:border-gray-700">
            {task.status !== 'done' && task.status !== 'archived' && (
              <button
                onClick={handleMarkComplete}
                disabled={markingComplete}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                {markingComplete ? 'Marking...' : 'Mark Complete'}
              </button>
            )}
            <button
              onClick={() => onEditTask(taskId)}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Edit Task
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GanttTaskDetailPanel;
